/**
 * VIP Mailing Service - API client for VIP mailing endpoints
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  }
}

export interface VipStatus {
  hasSubscription: boolean
  subscription: {
    id: string
    status: string
    current_period_end: string
    cancel_at_period_end: boolean
    is_paused: boolean
    amount: number
  } | null
  isActive: boolean
  isPaused: boolean
  hasMailingAddress: boolean
  address: {
    full_name: string
    address_line1: string
    address_line2: string | null
    city: string
    state: string
    zip_code: string
  } | null
}

export interface VipLetter {
  id: string
  title: string
  description: string
  month: string
  status: 'pending' | 'sent' | 'skipped'
  sent_at: string | null
  admin_notes: string | null
  created_at: string
}

export async function registerVipInterest(): Promise<{ success: boolean; alreadyRegistered: boolean; registeredAt: string }> {
  const response = await fetch(`${API_URL}/api/vip/register-interest`, {
    method: 'POST',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to register interest')
  return response.json()
}

export async function checkVipRegistration(): Promise<{ registered: boolean; registeredAt: string | null }> {
  const response = await fetch(`${API_URL}/api/vip/check-registration`, {
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to check registration')
  return response.json()
}

export async function verifyVipPin(pin: string): Promise<{ valid: boolean }> {
  const response = await fetch(`${API_URL}/api/vip/verify-pin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin })
  })
  if (!response.ok) throw new Error('Failed to verify PIN')
  return response.json()
}

export async function getVipStatus(): Promise<VipStatus> {
  const response = await fetch(`${API_URL}/api/vip/status`, {
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to get VIP status')
  return response.json()
}

export async function createVipSubscription(paymentMethodId: string): Promise<{ success: boolean; requiresAction?: boolean; clientSecret?: string }> {
  const response = await fetch(`${API_URL}/api/vip/subscribe`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ paymentMethodId })
  })
  const data = await response.json()
  if (!response.ok) throw new Error(data.error || 'Failed to subscribe')
  return data
}

export async function pauseVipSubscription(): Promise<{ success: boolean }> {
  const response = await fetch(`${API_URL}/api/vip/pause`, {
    method: 'POST',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to pause subscription')
  return response.json()
}

export async function resumeVipSubscription(): Promise<{ success: boolean }> {
  const response = await fetch(`${API_URL}/api/vip/resume`, {
    method: 'POST',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to resume subscription')
  return response.json()
}

export async function cancelVipSubscription(): Promise<{ success: boolean }> {
  const response = await fetch(`${API_URL}/api/vip/cancel`, {
    method: 'POST',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to cancel subscription')
  return response.json()
}

// ==========================================
// VIP Progress (Status Dashboard)
// ==========================================

export interface VipProgress {
  status: 'member' | 'vip'
  last_request_at: string | null
  request_count: number
  can_request: boolean
  next_request_at: string | null
}

export async function getVipProgress(): Promise<VipProgress> {
  const response = await fetch(`${API_URL}/api/vip/progress`, {
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to get VIP progress')
  return response.json()
}

export async function requestVipAccess(): Promise<{ success: boolean; cooldown?: boolean; next_request_at?: string; requested_at?: string }> {
  const response = await fetch(`${API_URL}/api/vip/request-access`, {
    method: 'POST',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to request VIP access')
  return response.json()
}

export async function startGallerySession(): Promise<{ session_id: string }> {
  const response = await fetch(`${API_URL}/api/vip/gallery-session/start`, {
    method: 'POST',
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to start gallery session')
  return response.json()
}

export async function endGallerySession(sessionId: string): Promise<{ counted: boolean }> {
  const response = await fetch(`${API_URL}/api/vip/gallery-session/end`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ session_id: sessionId })
  })
  if (!response.ok) throw new Error('Failed to end gallery session')
  return response.json()
}

export async function heartbeatGallerySession(sessionId: string): Promise<void> {
  const response = await fetch(`${API_URL}/api/vip/gallery-session/heartbeat`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ session_id: sessionId })
  })
  if (!response.ok) throw new Error('Failed to heartbeat gallery session')
}

// Fire-and-forget version for tab close / beforeunload (uses keepalive)
export function endGallerySessionKeepalive(sessionId: string): void {
  try {
    fetch(`${API_URL}/api/vip/gallery-session/end`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ session_id: sessionId }),
      keepalive: true
    }).catch(() => {})
  } catch {
    // Silently fail on tab close
  }
}

export async function getVipLetters(): Promise<VipLetter[]> {
  const response = await fetch(`${API_URL}/api/vip/letters`, {
    headers: getAuthHeaders()
  })
  if (!response.ok) throw new Error('Failed to fetch letters')
  const data = await response.json()
  return data.letters || []
}
